/**
 * The rows of the Library's list: a score, and a work kept in more than one score.
 *
 * A work is one row however many scores of it the library holds -- a symphony's movements,
 * a set's songs, every community transcription of a piece -- and opens in place to list them,
 * each with its own licence, its own links and its own way to Arrange, since each is a
 * different file. A work kept in one score is that score's row and has nothing to open.
 */

import { useId, useState } from "react";
import { Link } from "react-router-dom";
import type { LibraryEntry, LibraryWork } from "@app/shared-types";
import { bytes, count } from "../lib/format";
import {
  arrangeHref,
  dated,
  editionLabel,
  inside,
  placing,
  sourceLabel,
  transcribed,
  withinWork,
} from "../lib/library";

const EXTERNAL = "underline decoration-rule underline-offset-2 hover:text-ink";

function lifespan(entry: LibraryEntry): string {
  if (entry.born === null && entry.died === null) return "";
  return ` (${entry.born ?? "?"}–${entry.died ?? "?"})`;
}

/** The part names as a line, or a count where there are too many to read as one. */
function scoring(parts: readonly string[]): string {
  if (parts.length === 0) return "";
  if (parts.length > 6) return `${parts.length} parts`;
  return parts.join(", ");
}

/** One score. `within` is set where it is listed under its work, which is named above it. */
function ScoreRow({ entry, within }: { entry: LibraryEntry; within: boolean }): JSX.Element {
  const named = within ? withinWork(entry) : { title: entry.title, where: placing(entry) };
  const people = [
    within ? null : `${entry.composer}${lifespan(entry)}`,
    entry.lyricist === null ? null : `words by ${entry.lyricist}`,
  ].filter((one) => one !== null);
  return (
    <li
      className={`flex flex-col gap-2 border-b border-rule-faint py-3 pr-4 last:border-b-0 sm:flex-row sm:items-center sm:gap-4 ${
        within ? "pl-7 sm:pl-[60px]" : "pl-4"
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm leading-snug">
          <span className="font-medium">{named.title}</span>
          {named.where === null ? null : <span className="text-ink-faded"> · {named.where}</span>}
        </div>
        {people.length === 0 ? null : (
          <div className="mt-0.5 text-xs text-ink-faded">{people.join(" · ")}</div>
        )}
        <div className="mt-1 text-xs text-ink-quiet">
          {[scoring(entry.parts), transcribed(entry), dated(entry), bytes(entry.size)]
            .filter(Boolean)
            .join(" · ")}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-ink-quiet">
          <a className={EXTERNAL} href={entry.licenceUrl} target="_blank" rel="noreferrer">
            {entry.licence}
          </a>
          <a className={EXTERNAL} href={entry.sourceUrl} target="_blank" rel="noreferrer">
            {sourceLabel(entry)}
          </a>
          {entry.editionUrl === null ? null : (
            <a className={EXTERNAL} href={entry.editionUrl} target="_blank" rel="noreferrer">
              {editionLabel(entry)}
            </a>
          )}
        </div>
      </div>
      <Link
        to={arrangeHref(entry.id)}
        className="inline-flex h-9 shrink-0 items-center justify-center rounded-full bg-ink px-4 text-[13px] font-medium text-paper hover:brightness-125"
      >
        Arrange
      </Link>
    </li>
  );
}

export interface WorkRowProps {
  readonly work: LibraryWork;
  /** Open until the reader says otherwise: the one work a search found is all there is to read. */
  readonly opened: boolean;
}

export default function WorkRow({ work, opened }: WorkRowProps): JSX.Element | null {
  const [chosen, setChosen] = useState<boolean | null>(null);
  const open = chosen ?? opened;
  const list = useId();
  const first = work.scores[0];
  if (first === undefined) return null;
  if (work.scores.length === 1) return <ScoreRow entry={first} within={false} />;
  return (
    <li className="border-b border-rule-faint last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={list}
        onClick={() => setChosen(!open)}
        className={`flex min-h-16 w-full items-center gap-3 px-4 py-3 text-left hover:bg-paper ${
          open ? "bg-paper" : ""
        }`}
      >
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-rule bg-leaf text-ink-faded">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={open ? "rotate-90" : ""}
          >
            <path d="M6 3.5 10.5 8 6 12.5" />
          </svg>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium leading-snug">{work.name}</span>
          <span className="mt-0.5 block text-xs text-ink-faded">
            {first.composer}
            {lifespan(first)}
          </span>
          <span className="mt-1 block text-xs text-ink-quiet">{inside(work.scores)}</span>
        </span>
        <span className="shrink-0 text-xs text-ink-faded">
          {open ? "Hide" : `${count(work.scores.length)} scores`}
        </span>
      </button>
      {open ? (
        <ul id={list} className="border-t border-rule-faint">
          {work.scores.map((entry) => (
            <ScoreRow key={entry.id} entry={entry} within />
          ))}
        </ul>
      ) : null}
    </li>
  );
}
